import { ContentCopy, InfoOutlined, PushPin, Refresh, ViewTimeline } from '@mui/icons-material';
import { Alert, Box, BoxProps, IconButton, Stack, Tab, Tabs } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { grey, red } from '@mui/material/colors';
import React from 'react';
import { useAppSelector } from '../appHooks';
import { mergeDataGridConfig } from '../styles/DataGridConfig';
import { CopyToClipboard } from '../common/CopyToClipBoard';
import NavigableHtml from '../common/NavigableHtml';
import { ActiveMore } from '../common/PinIcon';
import { SearchParameters, setFavorite } from '../redux/cutterSearchAPI';
import { useGetCutterHistoryBySerialQuery, cutterHistoryApi } from '../redux/cutterHistoryAPI';
import store, { RootState } from '../store';
import { CutterHistory } from '../types/cutterHistory';
import { useBookmarkNavigation } from '../types/cutterState';
import { useNavigate } from 'react-router-dom';
import LogToDB from '../types/dbLogs';
import StepDiagnostic from './StepDiagnostic';
import CutterDetailsHistoryConnexion from './connexion/CutterDetailsHistory.connexion';
import CutterDetailsHistorySystem from './system/CutterDetailsHistory.system';
import CutterDetailsHistoryPaperwork from './paperwork/CutterDetailsHistory.paperwork';
import HelpIcon from '../help/HelpIcon';
import CutterDetailsHistoryNetwork from './factory/CutterDetailsHistory.network';
import CutterDetailsHistoryMaintenance from './maintenance/CutterDetailsHistory.maintenance';
import CutterTimelinePanel from './CutterTimeline';
import CutterDetailsHistoryInfoPanel from './CutterDetailsHistoryInfo';


// Unique style for all 4 grid cells
const gridCellStyle = {
    margin: "0px",
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center"
};


export interface CutterDetailsHistoryProps extends BoxProps {
    serialNumber: string;
    verticalTabValue: string;
    onVerticalTabChange: (value: string) => void;
    sx?: BoxProps['sx'];
}


export const CutterDetailsHistory: React.FC<CutterDetailsHistoryProps> = (props: CutterDetailsHistoryProps) => {
    const { serialNumber, verticalTabValue, onVerticalTabChange, sx } = props;

    const search: SearchParameters = useAppSelector((state: RootState) => state.persistedSearchReducer);
    const cutterQueryApi: ReturnType<typeof useGetCutterHistoryBySerialQuery> = useGetCutterHistoryBySerialQuery(serialNumber, { skip: !serialNumber });

    const cutter: CutterHistory | undefined = !cutterQueryApi.data ? undefined : cutterQueryApi.data[serialNumber];

    const favoriteState = cutter?.serialNumber ? search.favorites.some((serialNumber: string) => serialNumber === cutter.serialNumber) : false;
    const isVersalis: boolean = cutter?.state?.harD_Model ? cutter.state.harD_Model === 'Versalis' : false;



    const navigate = useNavigate();
    const [horizontalTabValue, setHorizontalTabValue] = React.useState('cutterReports');
    const [timelineOpen, setTimelineOpen] = React.useState(() => localStorage.getItem('timeline:open') !== 'false');
    const [timelineBrushRange, setTimelineBrushRange] = React.useState<[Date, Date] | null>(null);
    const [infoOpen, setInfoOpen] = React.useState(() => localStorage.getItem('cutter-info:open') !== 'false');

    React.useEffect(() => { localStorage.setItem('timeline:open', String(timelineOpen)); }, [timelineOpen]);
    React.useEffect(() => { localStorage.setItem('cutter-info:open', String(infoOpen)); }, [infoOpen]);

    // Reset zoom when the cutter context changes
    React.useEffect(() => { setTimelineBrushRange(null); }, [serialNumber]);

    const { subtab: urlSubtab, rowId: urlRowId, rowSource: urlRowSource } = useBookmarkNavigation();
    React.useEffect(() => {
        if (urlSubtab) setHorizontalTabValue(urlSubtab);
    }, [urlSubtab]);

    // Set default horizontal tab for each vertical section
    const defaultTabs = {
        'connexion': 'cutterReports',
        'system': 'software',
        'paperwork': 'salesforce',
        'maintenance': 'acts',
        'factory': ''
    };

    const handleVerticalTabChange = (_event: React.SyntheticEvent, newValue: string): void => {
        const newSubtab = defaultTabs[newValue as keyof typeof defaultTabs] || 'cutterReports';
        onVerticalTabChange(newValue);
        setHorizontalTabValue(newSubtab);
        navigate(`/bookmarks?serialnumber=${encodeURIComponent(serialNumber)}&tab=${encodeURIComponent(newValue)}&subtab=${encodeURIComponent(newSubtab)}`);
        void LogToDB(newValue, serialNumber);
    };

    const handleHorizontalTabChange = (_event: React.SyntheticEvent, newValue: string): void => {
        setHorizontalTabValue(newValue);
        navigate(`/bookmarks?serialnumber=${encodeURIComponent(serialNumber)}&tab=${encodeURIComponent(verticalTabValue)}&subtab=${encodeURIComponent(newValue)}`);
        void LogToDB(newValue, serialNumber);
    };


    if (!cutter?.serialNumber) {
        return (<Box></Box>)
    }
    else {
        defaultTabs.paperwork = cutter.salesforceCases.length > 0 ? 'salesforce' : 'contact';
        defaultTabs.system = cutter.errorsCount > 0 ? 'errors' : 'software';
    }

    // Common sx props for all TabPanels
    const tabPanelSx = {
        p: 0,
        minHeight: '400px',
        minWidth: 0,
        overflow: 'hidden'
    };

    // Common props for horizontal Tabs components
    const horizontalTabsProps = {
        sx: {
            borderTopColor: 'secondary.main',
            borderTopWidth: '0px',
            borderBottomWidth: '0.0px',
            borderLeftWidth: '0px',
            borderRightWidth: '0px',
            borderStyle: 'solid'
        },
        textColor: 'secondary' as const,
        indicatorColor: 'secondary' as const
    };

    // Common props for all DataGrids - using shared configuration
    const commonDataGridProps = mergeDataGridConfig({
        getRowId: (row: any) => row.rowNumber,
        checkboxSelection: false,
        initialState: { pagination: { paginationModel: { pageSize: 10 } } },
        pageSizeOptions: [10, 25, 50],
        sx: {
            minHeight: '400px',
            minWidth: 0,
            width: '100%',
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': { display: 'none' }
        },
        slots: {
            toolbar: mergeDataGridConfig().slots?.toolbar,
            noRowsOverlay: () => <Alert severity='info'>No data to display</Alert>,
            noResultsOverlay: () => <Alert severity='info'>No data to display</Alert>,
        }
    });

    return (
        <>
        <Box component={Stack} direction='column' sx={sx} paddingY={1} alignItems='space-between'>
            <Stack direction="row" useFlexGap flexWrap='nowrap' marginY={1} sx={{
                justifyContent: "space-between",
            }} >
                <Box display="grid" marginTop="-1rem" gridTemplateColumns="repeat(2, 1fr)" gridTemplateRows="repeat(2, 1fr)" gap={0} >
                    <Box sx={gridCellStyle}>
                        <ActiveMore aria-label="bookmark"
                            active={favoriteState}
                            degrees={-90} onClick={() => store.dispatch(setFavorite(serialNumber))}>
                            <PushPin titleAccess={`${(!favoriteState ? 'Add as bookmark' : 'Remove from bookmarks')}`}
                                sx={{ color: (favoriteState ? red[800] : 'inherit'), marginTop: '0.3rem' }} />
                        </ActiveMore>
                    </Box>
                    <Box sx={gridCellStyle}>
                            <IconButton data-tut="tabSearch_Btn" sx={{ width: '2em', height: '2em', }} title="Reload" onClick={e => store.dispatch(cutterHistoryApi.util.invalidateTags(['CutterHistory', 'ContractualData', 'FactoryNetwork']))}>
                            <Refresh color="inherit" />
                        </IconButton>
                    </Box>
                    <Box sx={gridCellStyle}>
                        <IconButton sx={{ width: '2em', height: '2em' }} title={timelineOpen ? 'Close Timeline' : 'Open Timeline'} onClick={() => { if (!timelineOpen) void LogToDB('timeline', serialNumber); setTimelineOpen(v => !v); }}>
                            <ViewTimeline color={timelineOpen ? 'primary' : 'inherit'} />
                        </IconButton>
                    </Box>
                    <Box sx={gridCellStyle}>
                        <IconButton sx={{ width: '2em', height: '2em' }} title={infoOpen ? 'Close Cutter info' : 'Open Cutter info'} onClick={() => { if (!infoOpen) void LogToDB('cutterIdentity', serialNumber); setInfoOpen(v => !v); }}>
                            <InfoOutlined color={infoOpen ? 'primary' : 'inherit'} fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
                <StepDiagnostic cutter={cutter} />
            </Stack>
            <Alert severity='warning' sx={{
                display: (cutter.state?.diagnostics
                    && !cutter.state.diagnostics.isValid
                    && cutter.state.diagnostics.failures.length > 0
                    ? 'flex' : 'none'), marginX: 2, marginTop: 0, marginBottom: 1, whiteSpace: 'pre-line'
            }} >
                <>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <strong>{`${cutter.integration.zipReport ?? 'Current issue'} :`}</strong>
                        <CopyToClipboard
                            TextToCopy={`${cutter.integration.serialNumber}\t${cutter.integration.zipReport}\t${cutter.state?.diagnostics?.failures.map(f => f.errorMessage).join('\n')}`} title='Copy to clipboard'
                            AckMessage='Log copied to clipboard'>
                            <ContentCopy sx={{ color: 'warning', padding: 0, fontSize: '1rem' }} titleAccess="Copy logs" />
                        </CopyToClipboard>
                    </Stack>
                    {/* All Diagnostics Validation failures (.Net controllers) */}
                    <ul style={{ margin: '8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>
                        {cutter.state?.diagnostics?.failures.map((failure, index) => (
                            <li key={index} style={{ marginBottom: '4px' }}>
                                <NavigableHtml html={failure.errorMessage} />
                            </li>
                        ))}
                    </ul>
                </>
            </Alert>
            <Box display="flex" sx={{ width: '100%', backgroundColor: 'background.paper' }}>
                {/* Vertical Tabs */}
                <TabContext value={verticalTabValue} key='historyTabs'>
                    <Tabs
                        value={verticalTabValue}
                        onChange={handleVerticalTabChange}
                        orientation="vertical"
                        sx={{
                            paddingTop: '2px',
                            borderColor: 'divider',
                            borderStyle: 'solid',
                            borderWidth: '0.5px',
                            minWidth: '120px',
                            flexShrink: 0,
                            backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[100] : '#252525',

                            '& .MuiTabs-indicator': {
                                left: 2,
                                right: 'auto',
                                width: '6px',
                                height: '8px'
                            }
                        }}
                        textColor="secondary"
                        indicatorColor="secondary"
                        aria-label="Main category tabs">
                        <Tab label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                Connection
                                <HelpIcon helpContext="details-connection" placement="right" size="small" />
                            </Stack>
                        } value='connexion' />
                        <Tab label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                System
                                <HelpIcon helpContext="details-factory" placement="right" size="small" />
                            </Stack>
                        } value='system' />
                        <Tab label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                Maintenance
                                <HelpIcon helpContext="details-maintenance" placement="right" size="small" />
                            </Stack>
                        } sx={{ whiteSpace: 'nowrap' }} value='maintenance' />
                        <Tab label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                Paperwork
                                <HelpIcon helpContext="details-paperwork" placement="right" size="small" />
                            </Stack>
                        } sx={{ whiteSpace: 'nowrap' }} value='paperwork' />
                        {/* This tab should show cutter factory topology diagram with cutters and network settings 
                            Include factory tabpanel from the administrative tab fto be updated when the environment 
                            will be available
                        */}
                        <Tab  label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                Factory  
                                <HelpIcon helpContext="details-factory" placement="right" size="small" />
                            </Stack>
                        } sx={{ whiteSpace: 'nowrap' }} value='factory' />
                    </Tabs>

                    {/* Content Area */}
                    <Box sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        borderColor: 'divider',
                        borderBottomWidth: '0.5px',
                        borderStyle: 'solid'
                    }}>
                        {/* Connexion Tab Panel */}
                        <TabPanel value='connexion' sx={tabPanelSx}>
                            <CutterDetailsHistoryConnexion
                                cutter={cutter}
                                isVersalis={isVersalis}
                                horizontalTabValue={horizontalTabValue}
                                onHorizontalTabChange={handleHorizontalTabChange}
                                commonDataGridProps={commonDataGridProps}
                                horizontalTabsProps={horizontalTabsProps}
                                tabPanelSx={tabPanelSx}
                                selectedRowId={verticalTabValue === 'connexion' ? urlRowId : null}
                            />
                        </TabPanel>

                        {/* System Tab Panel */}
                        <TabPanel value='system' sx={tabPanelSx}>
                            <CutterDetailsHistorySystem
                                cutter={cutter}
                                isVersalis={isVersalis}
                                horizontalTabValue={horizontalTabValue}
                                onHorizontalTabChange={handleHorizontalTabChange}
                                commonDataGridProps={commonDataGridProps}
                                horizontalTabsProps={horizontalTabsProps}
                                tabPanelSx={tabPanelSx}
                                selectedRowId={verticalTabValue === 'system' ? urlRowId : null}
                                selectedRowSource={verticalTabValue === 'system' ? urlRowSource : ''}
                            />
                        </TabPanel>

                        {/* Maintenance Tab Panel */}
                        <TabPanel value='maintenance' sx={tabPanelSx}>
                            <CutterDetailsHistoryMaintenance
                                cutter={cutter}
                                isVersalis={isVersalis}
                                horizontalTabValue={horizontalTabValue}
                                onHorizontalTabChange={handleHorizontalTabChange}
                                commonDataGridProps={commonDataGridProps}
                                horizontalTabsProps={horizontalTabsProps}
                                tabPanelSx={tabPanelSx}
                                selectedRowId={verticalTabValue === 'maintenance' ? urlRowId : null}
                            />
                        </TabPanel>

                        {/* Administrative Tab Panel */}
                        <TabPanel value='paperwork' sx={tabPanelSx}>
                            <CutterDetailsHistoryPaperwork
                                cutter={cutter}
                                isVersalis={isVersalis}
                                horizontalTabValue={horizontalTabValue}
                                onHorizontalTabChange={handleHorizontalTabChange}
                                commonDataGridProps={commonDataGridProps}
                                horizontalTabsProps={horizontalTabsProps}
                                tabPanelSx={tabPanelSx}
                            />
                        </TabPanel>
                        <TabPanel value='factory' sx={tabPanelSx}>
                            <CutterDetailsHistoryNetwork serialNumber={cutter.serialNumber} width='100%' height='65vh' />
                        </TabPanel>
                    </Box>
                </TabContext>
            </Box>
        </Box>
        {timelineOpen && cutter && (
        <CutterTimelinePanel open={timelineOpen} onClose={() => setTimelineOpen(false)} cutter={cutter}
                brushRange={timelineBrushRange} onBrushRangeChange={setTimelineBrushRange} />
        )}
        {infoOpen && cutter && (
        <CutterDetailsHistoryInfoPanel open={infoOpen} onClose={() => setInfoOpen(false)} cutter={cutter} />
        )}
        </>
    );
}



export default CutterDetailsHistory;

